import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Trophy, Calendar, Clock, FileText, Paperclip, Trash2, X, ChevronRight,
  Award, Download, Check, Camera, Share2,
  Edit3,
} from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { martialArts, getArtById } from '../data/training';
import GearRecommendations from '../components/GearRecommendations';
import WidgetGate from '../components/WidgetGate';
import PromotionShareCard from '../components/PromotionShareCard';
import MediaCaptureMenu from '../components/MediaCaptureMenu';
import LogSessionDialog from '../components/Combatives/LogSessionDialog';
import HistoryClampBanner from '../components/HistoryClampBanner';
import BJJLogDialog from '../components/Combatives/BJJLogDialog';
import BJJSessionDetail from '../components/Combatives/BJJSessionDetail';
import MuayThaiLogDialog from '../components/Combatives/MuayThaiLogDialog';
import MuayThaiSessionDetail from '../components/Combatives/MuayThaiSessionDetail';
import JudoLogDialog from '../components/Combatives/JudoLogDialog';
import JudoSessionDetail from '../components/Combatives/JudoSessionDetail';
import WrestlingLogDialog from '../components/Combatives/WrestlingLogDialog';
import WrestlingSessionDetail from '../components/Combatives/WrestlingSessionDetail';
import BoxingLogDialog from '../components/Combatives/BoxingLogDialog';
import BoxingSessionDetail from '../components/Combatives/BoxingSessionDetail';
import { downloadBJJCSV, downloadGenericCSV, downloadMuayThaiCSV, downloadJudoCSV, downloadWrestlingCSV, downloadBoxingCSV } from '../lib/csvExport';
import SharedAttachmentTile from '../components/AttachmentTile';
import { useTierState } from '../hooks/useTierState';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import PaywallModal from '../components/PaywallModal';
import { Sparkles, Lock } from 'lucide-react';

// Arts that ship with a detailed (paid) journal dialog + per-art rich
// CSV exporter. Free users on these arts still get the generic basic
// `LogSessionDialog`; the rich UX is gated behind `combatives_detailed`.
// When a new art ships its rich journal (Wrestling, Boxing, etc.), add
// its art.id here — the upsell pill, edit gate, dialog router, and CSV
// branch all key off this constant so wiring stays consistent.
const RICH_JOURNAL_ARTS = new Set(['bjj', 'muay-thai', 'judo', 'wrestling', 'boxing']);

// Per-art CSV exporter lookup. Adding a new detailed art (Karate, TKD,
// Sambo, etc.) is now a one-line addition here in addition to the
// RICH_JOURNAL_ARTS set + the dialog/detail routers below. Free users
// or arts without their own exporter fall through to downloadGenericCSV.
const CSV_EXPORTERS = {
  'bjj': downloadBJJCSV,
  'muay-thai': downloadMuayThaiCSV,
  'judo': downloadJudoCSV,
  'wrestling': downloadWrestlingCSV,
  'boxing': downloadBoxingCSV,
};

const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024; // 3MB per file cap (localStorage friendly)

// Format a date difference as human-readable rank tenure: "11mo", "2y 3mo", "8d"
const formatTenure = (fromISO, toDate = new Date()) => {
  if (!fromISO) return null;
  const from = new Date(fromISO);
  if (Number.isNaN(from.getTime())) return null;
  const ms = toDate.getTime() - from.getTime();
  if (ms < 0) return 'today';
  const days = Math.floor(ms / 86400000);
  if (days < 31) return days === 0 ? 'today' : `${days}d`;
  const months = Math.floor(days / 30.437);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(months / 12);
  const remMo = months % 12;
  return remMo === 0 ? `${years}y` : `${years}y ${remMo}mo`;
};

const formatPromotionDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const TrainingPage = () => {
  const { profile, update, training, addTrainingSession, deleteTrainingSession, updateTrainingSession, updateRank, addPromotion, deletePromotion } = useProfile();
  const navigate = useNavigate();
  const { is_paid: isPaid } = useTierState();
  const { enabled: judoDetailedEnabled } = useFeatureFlag('judo_detailed');
  const { enabled: muayThaiDetailedEnabled } = useFeatureFlag('muay_thai_detailed');
  const { enabled: wrestlingDetailedEnabled } = useFeatureFlag('wrestling_detailed');
  const { enabled: boxingDetailedEnabled } = useFeatureFlag('boxing_detailed');
  const selectedArts = profile.martialArts || [];
  const [activeArt, setActiveArt] = useState(selectedArts[0] || null);
  const [showArtPicker, setShowArtPicker] = useState(selectedArts.length === 0);
  const [logOpen, setLogOpen] = useState(false);
  // editingSession holds the session record to edit; when set, the dialog
  // opens prefilled and the save handler updates instead of creating.
  const [editingSession, setEditingSession] = useState(null);
  // Combatives detailed-journal paywall. Surfaced when a free user attempts
  // to edit a rich BJJ / Muay Thai session they (or admin via comp) logged
  // while on the paid tier. New logs route to the basic dialog automatically.
  const [detailedPaywallOpen, setDetailedPaywallOpen] = useState(false);

  const art = activeArt ? getArtById(activeArt) : null;
  const currentRank = (profile.ranks || {})[activeArt] || {};
  const sessionsForArt = useMemo(
    () => training.sessions
      .filter((s) => s.artId === activeArt)
      // Newest first. addTrainingSession prepends to the array, but cloud
      // hydrate / sync merges can break that order — sort by date desc to
      // guarantee the most recent session is on top regardless of how the
      // entry got into local state.
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [training.sessions, activeArt]
  );

  const toggleArt = (id) => {
    const next = selectedArts.includes(id)
      ? selectedArts.filter((x) => x !== id)
      : [...selectedArts, id];
    update({ martialArts: next });
    if (!next.includes(activeArt)) setActiveArt(next[0] || null);
    else if (!activeArt && next.length) setActiveArt(next[0]);
  };

  // No arts selected → show picker page
  if (selectedArts.length === 0 || showArtPicker) {
    return (
      <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">Martial Arts</div>
            <h1 className="font-display text-6xl md:text-8xl tracking-wide leading-none mb-3">Pick your art.</h1>
            <p className="text-enf-text-secondary max-w-2xl">Enferno supports the practitioners. Select every discipline you train — you can rank, log, and reflect on each one separately.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {martialArts.map((m) => {
              const picked = selectedArts.includes(m.id);
              return (
                <button key={m.id} onClick={() => toggleArt(m.id)}
                  className={`group relative text-left overflow-hidden rounded-xl border transition-all ${
                    picked ? 'border-enf-accent-fg ring-2 ring-enf-accent/30' : 'border-enf-surface-border hover:border-enf-surface-border'
                  }`}>
                  <div className="aspect-[4/3] relative">
                    <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                    {picked && (
                      <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-enf-accent flex items-center justify-center">
                        <Trophy className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="font-display text-3xl tracking-wide leading-none mb-1 text-white">{m.name}</div>
                      <div className="text-xs text-white/80 line-clamp-2">{m.description}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedArts.length > 0 && (
            <button onClick={() => setShowArtPicker(false)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm uppercase tracking-wider transition-colors">
              Continue with {selectedArts.length} {selectedArts.length === 1 ? 'art' : 'arts'} <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      {/* Themed hero */}
      <div className="relative -mt-20 pt-20 mb-8 overflow-hidden">
        <div className="absolute inset-0">
          <img src={art.image} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${art.accent}40 0%, transparent 60%)` }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-10">
          <div className="text-sky-400 text-xs uppercase tracking-[0.3em] font-semibold mb-2">Martial Arts · {art.shortName}</div>
          <h1 className="font-display text-6xl md:text-8xl tracking-wide leading-none mb-3 text-white">{art.name}.</h1>
          <p className="text-white/80 max-w-2xl">{art.description}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <HistoryClampBanner />
        {/* Art tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {selectedArts.map((id) => {
            const m = getArtById(id);
            return (
              <button key={id} onClick={() => setActiveArt(id)}
                className={`px-4 py-2 rounded-md text-sm font-semibold uppercase tracking-wider transition-colors ${
                  activeArt === id ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-deep border border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                }`}>
                {m.shortName}
              </button>
            );
          })}
          <button onClick={() => setShowArtPicker(true)} className="px-3 py-2 rounded-md text-xs text-enf-text-muted hover:text-enf-role-info border border-dashed border-enf-surface-border hover:border-enf-role-info transition-colors flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add / edit arts
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Rank tracker */}
          <div className="lg:col-span-1 space-y-5">
            <RankCard
              art={art}
              currentRank={currentRank}
              promotions={(profile.promotionLog || {})[art.id] || []}
              athleteName={profile.socialHandle ? `@${profile.socialHandle.replace(/^@/, '')}` : (profile.name || profile.displayName || '')}
              premium={!!profile.premium}
              onChange={(rankId, stripes) => updateRank(art.id, rankId, stripes)}
              onAddPromotion={(entry) => addPromotion(art.id, entry)}
              onDeletePromotion={(promotionId) => deletePromotion(art.id, promotionId)}
            />

            {/* Export journal — replaces the cosmetic "Focus Areas" card
                that was just decorative. Per-art CSV download
                lives where the user already is — on the art surface —
                instead of buried in Profile. Hides when zero sessions are
                logged for the art (clean-by-default). BJJ uses the rich
                journal exporter; every other art uses the generic-shape
                exporter until it gets its own forked schema.
                G6: hidden entirely for Egnite (free) — CSV
                journal export is a paid feature. */}
            {isPaid && sessionsForArt.length > 0 && (
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5" data-testid="export-journal-card">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-4 h-4 text-enf-accent-fg" />
                  <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Export journal</div>
                </div>
                <p className="text-xs text-enf-text-muted mb-3">
                  Download every {art.shortName} session you've logged as a CSV file.
                </p>
                <button
                  onClick={() => {
                    // G6 + G7: rich CSV exporters only fire
                    // when the user is paid AND the art has a rich
                    // schema. Free users on rich-journal arts get the
                    // generic-shape exporter — their logs are basic
                    // Session Log entries anyway.
                    const exporter = isPaid && RICH_JOURNAL_ARTS.has(art.id) ? CSV_EXPORTERS[art.id] : null;
                    if (exporter) {
                      exporter(sessionsForArt);
                    } else {
                      downloadGenericCSV(sessionsForArt, art.id);
                    }
                    toast.success(`Exported ${sessionsForArt.length} ${art.shortName} session${sessionsForArt.length === 1 ? '' : 's'}`);
                  }}
                  data-testid="export-journal-button"
                  className="w-full px-3 py-2 bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-md text-sm font-medium text-enf-text-secondary hover:text-enf-text-primary transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download CSV
                </button>
              </div>
            )}

            {/* Gear recommendations for this art */}
            <WidgetGate page="training" id="affiliateGear">
              <GearRecommendations groupKey={art.id} />
            </WidgetGate>
          </div>

          {/* Sessions list */}
          <WidgetGate page="training" id="pastSessions" className="lg:col-span-2">
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-1">Session log</div>
                <div className="font-display text-3xl tracking-wide leading-none">{sessionsForArt.length} sessions</div>
              </div>
              <button onClick={() => setLogOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold uppercase tracking-wider transition-colors">
                <Plus className="w-4 h-4" /> Log session
              </button>
            </div>

            {/* G6 upsell — only for free users on BJJ / Muay Thai. Basic
                Session Log stays free; the rich discipline-specific journal
                is the Enferno upgrade. Copy is intentionally generic across
                all 8 arts so it scales as the remaining journals ship. */}
            {!isPaid && RICH_JOURNAL_ARTS.has(art.id) && (
              <button
                type="button"
                onClick={() => setDetailedPaywallOpen(true)}
                data-testid="combatives-detailed-upsell"
                className="w-full flex items-start gap-3 px-4 py-4 bg-enf-surface-deep hover:bg-enf-surface-mid border border-enf-surface-border hover:border-enf-accent-fg/40 rounded-xl text-left transition-colors group"
              >
                <Sparkles className="w-4 h-4 text-enf-accent-fg flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-enf-text-primary mb-1">
                    30 days of basic logs — or every session, forever.
                  </div>
                  <div className="text-xs text-enf-text-muted leading-relaxed">
                    Egnite stores 30 days of simple session logs. Enferno keeps your entire history and unlocks the full Combatives journal — built after researching the logs competitive martial artists actually keep. Pre-session intent → technique-by-technique breakdowns with position context → live sparring and round stats → honest post-session reflection → one-click CSV export. Personal Game Maps and position-to-submission decision trees — coming soon.
                  </div>
                </div>
                <Lock className="w-3.5 h-3.5 text-enf-text-muted group-hover:text-enf-accent-fg transition-colors flex-shrink-0 mt-0.5" />
              </button>
            )}

            {sessionsForArt.length === 0 ? (
              <div className="bg-enf-surface-deep border border-enf-surface-border border-dashed rounded-xl p-10 text-center">
                <FileText className="w-8 h-8 mx-auto mb-3 text-enf-text-muted" />
                <div className="font-display text-3xl tracking-wide leading-none mb-2">No sessions logged yet.</div>
                <div className="text-sm text-enf-text-muted mb-5 max-w-md mx-auto">Track what you drilled, what you rolled, what you learned. The mat doesn't forget — but your memory might.</div>
                <button onClick={() => setLogOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors">
                  <Plus className="w-4 h-4" /> Log first session
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sessionsForArt.map((s) => {
                  // G6: rich-journal edit is paid-only for BJJ/MT.
                  // Editing a rich session via the basic dialog would silently
                  // drop the rich fields, so we surface a paywall instead.
                  const isRichJournalSession = !!(s.intent || (s.techniques && s.techniques.length > 0) || (s.focusModes && s.focusModes.length > 0));
                  const editGated = !isPaid && RICH_JOURNAL_ARTS.has(art.id) && isRichJournalSession;
                  return (
                    <SessionCard
                      key={s.id}
                      session={s}
                      art={art}
                      onDelete={() => deleteTrainingSession(s.id)}
                      onEdit={() => (editGated ? setDetailedPaywallOpen(true) : setEditingSession(s))}
                    />
                  );
                })}
              </div>
            )}
          </div>
          </WidgetGate>
        </div>
      </div>

      {(logOpen || editingSession) && (
        // BJJ + Muay Thai have rich detailed-journal dialogs.
        // Other arts use the legacy generic dialog. art.id routes which
        // dialog is rendered. editingSession (when set) prefills the
        // dialog and changes save behavior to update-in-place.
        (() => {
          const commonOnClose = () => { setLogOpen(false); setEditingSession(null); };
          const commonOnSave = (data) => {
            if (editingSession) {
              updateTrainingSession(editingSession.id, data);
              toast.success('Session updated');
            } else {
              addTrainingSession({ ...data, artId: art.id, artName: art.name });
              toast.success('Session logged', {
                description: 'See it on the Progress page under Combatives.',
                action: { label: 'View', onClick: () => navigate('/progress') },
              });
            }
            commonOnClose();
          };
          if (art.id === 'bjj') {
            // G6: rich BJJ journal is an Enferno feature.
            // Egnite (free) gets the generic basic Session Log — same
            // experience the other arts ship with.
            if (!isPaid) {
              return <LogSessionDialog art={art} isPaid={isPaid} onUpsell={() => setDetailedPaywallOpen(true)} onClose={commonOnClose} onSave={commonOnSave} />;
            }
            return <BJJLogDialog art={art} initialData={editingSession} onClose={commonOnClose} onSave={commonOnSave} />;
          }
          if (art.id === 'muay-thai') {
            if (!isPaid || !muayThaiDetailedEnabled) {
              return <LogSessionDialog art={art} isPaid={isPaid} onUpsell={() => setDetailedPaywallOpen(true)} onClose={commonOnClose} onSave={commonOnSave} />;
            }
            return <MuayThaiLogDialog art={art} initialData={editingSession} onClose={commonOnClose} onSave={commonOnSave} />;
          }
          if (art.id === 'judo') {
            // G7: rich Judo journal is an Enferno feature.
            // Egnite gets the generic basic Session Log — same UX as
            // the other arts without dedicated dialogs.
            if (!isPaid || !judoDetailedEnabled) {
              return <LogSessionDialog art={art} isPaid={isPaid} onUpsell={() => setDetailedPaywallOpen(true)} onClose={commonOnClose} onSave={commonOnSave} />;
            }
            return <JudoLogDialog art={art} initialData={editingSession} onClose={commonOnClose} onSave={commonOnSave} />;
          }
          if (art.id === 'wrestling') {
            // G7: rich Wrestling journal is an Enferno feature.
            // Same gating shape as the other detailed arts — Egnite + flag-off
            // users fall back to the generic basic Session Log.
            if (!isPaid || !wrestlingDetailedEnabled) {
              return <LogSessionDialog art={art} isPaid={isPaid} onUpsell={() => setDetailedPaywallOpen(true)} onClose={commonOnClose} onSave={commonOnSave} />;
            }
            return <WrestlingLogDialog art={art} initialData={editingSession} onClose={commonOnClose} onSave={commonOnSave} />;
          }
          if (art.id === 'boxing') {
            // G7: rich Boxing journal is an Enferno feature.
            // Same gating shape as the other detailed arts — Egnite + flag-off
            // users fall back to the generic basic Session Log.
            if (!isPaid || !boxingDetailedEnabled) {
              return <LogSessionDialog art={art} isPaid={isPaid} onUpsell={() => setDetailedPaywallOpen(true)} onClose={commonOnClose} onSave={commonOnSave} />;
            }
            return <BoxingLogDialog art={art} initialData={editingSession} onClose={commonOnClose} onSave={commonOnSave} />;
          }
          return <LogSessionDialog art={art} isPaid={isPaid} onUpsell={() => setDetailedPaywallOpen(true)} onClose={commonOnClose} onSave={commonOnSave} />;
        })()
      )}
      <PaywallModal open={detailedPaywallOpen} onOpenChange={setDetailedPaywallOpen} feature="combatives_detailed" />
    </div>
  );
};

// ---------- Belt strip (visual representation of a single belt) ----------
// Renders the belt as a horizontal fabric strip with a tape band at the right
// end that holds the stripe markers. Dark belts get white tape, light belts
// get dark tape — same convention used in every gi belt I've ever owned.
const isDarkBeltColor = (hex) => {
  const c = (hex || '').toLowerCase();
  return c === '#0a0a0a' || c === '#78350f' || c === '#1e40af' || c === '#0c4a6e' ||
         c === '#6b21a8' || c === '#7c2d12' || c === '#581c87' || c === '#991b1b' ||
         c === '#2563eb' || c === '#0369a1';
};

const BeltStrip = ({ rank, stripes = 0, maxStripes = 0, height = 'h-7', glowAccent }) => {
  const color = rank?.color || '#27272a';
  const darkBelt = isDarkBeltColor(color);
  const tapeColor = darkBelt ? '#f5f5f4' : '#0a0a0a';
  const tapeBgOpacity = darkBelt ? 'bg-white/10' : 'bg-black/10';

  return (
    <div
      className={`relative ${height} w-full rounded-sm overflow-hidden shadow-md`}
      style={{
        backgroundColor: color,
        boxShadow: glowAccent
          ? `0 0 0 1px ${glowAccent}80, 0 0 24px -4px ${glowAccent}80, 0 6px 14px rgba(0,0,0,0.55)`
          : '0 4px 10px rgba(0,0,0,0.55)',
      }}
    >
      {/* Subtle fabric weave — diagonal stripes at low opacity */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0 2px, rgba(0,0,0,0.25) 2px 3px)`,
        }}
      />
      {/* Vertical stripe tape band — real gi belts have stripes as vertical
          tape strips on a black bar at the belt end. Tall + narrow, not pips. */}
      {maxStripes > 0 && (
        <div className={`absolute right-0 top-0 bottom-0 flex items-center justify-center gap-0.5 px-1 ${tapeBgOpacity}`}
          style={{ width: `${Math.min(40, maxStripes * 8 + 8)}%`, borderLeft: `1.5px solid ${tapeColor}40` }}>
          {Array.from({ length: maxStripes }).map((_, i) => {
            const filled = i < stripes;
            return (
              <div
                key={i}
                className="h-[80%] rounded-[1px]"
                style={{
                  width: '3px',
                  backgroundColor: filled ? tapeColor : 'transparent',
                  border: filled ? 'none' : `1px dashed ${tapeColor}60`,
                  marginRight: i < maxStripes - 1 ? '3px' : '0',
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const RankCard = ({ art, currentRank, promotions, athleteName, premium, onChange, onAddPromotion, onDeletePromotion }) => {
  const [editing, setEditing] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [shareCurrentOpen, setShareCurrentOpen] = useState(false);
  const currentRankObj = art.ranks.find((r) => r.id === currentRank.rankId);
  const currentIdx = art.ranks.findIndex((r) => r.id === currentRank.rankId);

  // Sort promotions ascending so we can find the latest event matching the
  // current rank — that's what tenure ("11 months at rank") is measured from.
  const sortedPromos = useMemo(
    () => [...(promotions || [])].sort((a, b) => (a.promotedAt || '').localeCompare(b.promotedAt || '')),
    [promotions]
  );
  const currentRankPromo = useMemo(() => {
    for (let i = sortedPromos.length - 1; i >= 0; i--) {
      if (sortedPromos[i].rankId === currentRank.rankId) return sortedPromos[i];
    }
    return null;
  }, [sortedPromos, currentRank.rankId]);
  const tenure = currentRankPromo ? formatTenure(currentRankPromo.promotedAt) : null;

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Award className="w-4 h-4 text-enf-accent-fg" /><div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Current rank</div></div>
        <button onClick={() => setEditing(!editing)} className="text-xs text-enf-role-info hover:text-enf-text-primary" data-testid="rank-edit-toggle">
          {editing ? 'Done' : currentRankObj ? 'Edit' : 'Set rank'}
        </button>
      </div>

      {!editing && currentRankObj && (
        <div>
          {/* The belt itself — large, art-accent glow, stripe tape band */}
          <div className="mb-3" data-testid="current-belt-strip">
            <BeltStrip
              rank={currentRankObj}
              stripes={currentRank.stripes || 0}
              maxStripes={art.hasStripes ? art.maxStripes : 0}
              height="h-9"
              glowAccent={art.accent}
            />
          </div>
          <div className="flex items-baseline justify-between mb-1">
            <div className="font-display text-2xl tracking-wide leading-none">{currentRankObj.name}</div>
            {art.hasStripes && (
              <div className="text-xs text-enf-text-muted font-mono">
                {currentRank.stripes || 0} / {art.maxStripes} stripes
              </div>
            )}
          </div>
          {/* Tenure line — only when we have a promotion record for this rank */}
          {tenure && currentRankPromo && (
            <div className="text-xs text-enf-text-muted mb-4" data-testid="rank-tenure">
              {tenure} at rank · since {formatPromotionDate(currentRankPromo.promotedAt)}
              {currentRankPromo.school && <span> · {currentRankPromo.school}</span>}
            </div>
          )}
          {!tenure && <div className="mb-4" />}

          {/* Rank ladder — full progression, you-are-here highlight */}
          <div className="pt-4 border-t border-enf-surface-border">
            <div className="text-[10px] uppercase tracking-[0.25em] text-enf-text-muted font-semibold mb-2.5">Your road</div>
            <div className="flex items-stretch gap-1" data-testid="rank-ladder">
              {art.ranks.map((r, i) => {
                const isPast = i < currentIdx;
                const isCurrent = i === currentIdx;
                return (
                  <div
                    key={r.id}
                    title={r.name}
                    className="flex-1 h-5 rounded-[2px] transition-all"
                    style={{
                      backgroundColor: isPast || isCurrent ? r.color : 'transparent',
                      border: isCurrent ? `2px solid ${art.accent}` : isPast ? `1px solid ${r.color}` : `1px dashed #3f3f46`,
                      opacity: isPast ? 0.55 : 1,
                      boxShadow: isCurrent ? `0 0 12px -2px ${art.accent}` : 'none',
                    }}
                  />
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[10px] text-enf-text-muted font-mono">
              <span>{art.ranks[0].name.split(' ')[0]}</span>
              <span className="text-enf-text-muted">
                {currentIdx + 1} / {art.ranks.length}
              </span>
              <span>{art.ranks[art.ranks.length - 1].name.split(' ')[0]}</span>
            </div>
          </div>

          {/* Log a promotion — the ceremonial entry point. Adjacent share button
              for current-rank state (no promotion log entry required — for users
              like established black belts who join Enferno without backfilling history). */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              onClick={() => setPromoOpen(true)}
              data-testid="log-promotion-cta"
              className="py-2.5 bg-enf-accent/10 hover:bg-enf-accent border border-enf-accent-fg/40 hover:border-enf-accent-fg text-enf-accent-fg hover:text-enf-text-primary rounded-md text-sm font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
              style={{ boxShadow: `0 0 0 1px ${art.accent}30` }}
            >
              <Trophy className="w-4 h-4" /> Log promotion
            </button>
            <button
              onClick={() => setShareCurrentOpen(true)}
              data-testid="share-current-rank-cta"
              title="Share your current rank as an image — no log entry required"
              className="py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border hover:border-enf-role-info/50 text-enf-text-secondary hover:text-enf-role-info rounded-md text-sm font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" /> Share rank
            </button>
          </div>

          {/* Promotion timeline */}
          {sortedPromos.length > 0 && (
            <PromotionTimeline
              art={art}
              promotions={sortedPromos}
              athleteName={athleteName}
              premium={premium}
              onDelete={onDeletePromotion}
            />
          )}
        </div>
      )}

      {!editing && !currentRankObj && (
        <div className="space-y-2">
          <button
            onClick={() => setEditing(true)}
            data-testid="rank-set-cta"
            className="w-full p-3 border border-dashed border-enf-surface-border rounded-md text-sm text-enf-text-muted hover:text-enf-role-info hover:border-enf-role-info transition-colors"
          >
            Set your current rank →
          </button>
          <button
            onClick={() => setPromoOpen(true)}
            data-testid="log-first-promotion-cta"
            className="w-full p-3 border border-dashed border-enf-accent-fg/40 hover:border-enf-accent-fg rounded-md text-sm text-enf-accent-fg hover:text-enf-text-primary hover:bg-enf-accent transition-colors flex items-center justify-center gap-2"
          >
            <Trophy className="w-4 h-4" /> Log a promotion event
          </button>
        </div>
      )}

      {editing && (
        <div className="space-y-2">
          {art.ranks.map((r) => {
            const isCurrent = r.id === currentRank.rankId;
            return (
              <button key={r.id} onClick={() => onChange(r.id, currentRank.stripes || 0)}
                data-testid={`rank-option-${r.id}`}
                className={`w-full flex items-center gap-3 p-2 rounded-md border transition-colors ${
                  isCurrent ? 'border-enf-accent-fg bg-enf-accent/10' : 'border-enf-surface-border hover:border-enf-surface-border'
                }`}>
                <div className="w-20 flex-shrink-0">
                  <BeltStrip rank={r} stripes={0} maxStripes={0} height="h-5" />
                </div>
                <div className="text-sm font-medium flex-1 text-left">{r.name}</div>
                {isCurrent && <Check className="w-4 h-4 text-enf-accent-fg flex-shrink-0" />}
              </button>
            );
          })}
          {art.hasStripes && currentRankObj && (
            <div className="pt-3 mt-3 border-t border-enf-surface-border">
              <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Stripes</div>
              <div className="flex gap-2">
                {Array.from({ length: art.maxStripes + 1 }).map((_, n) => (
                  <button key={n} onClick={() => onChange(currentRank.rankId, n)}
                    data-testid={`stripe-${n}`}
                    className={`flex-1 py-2 rounded text-xs font-bold transition-colors ${
                      (currentRank.stripes || 0) === n ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-mid text-enf-text-secondary hover:text-enf-text-primary'
                    }`}>{n}</button>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs text-enf-text-muted pt-2 italic">
            Editing current state — use <strong className="text-enf-accent-fg not-italic">Log promotion</strong> to record a ceremony with date, school, and photo.
          </div>
        </div>
      )}

      {promoOpen && (
        <PromotionDialog
          art={art}
          currentRank={currentRank}
          onClose={() => setPromoOpen(false)}
          onSave={(entry) => { onAddPromotion(entry); setPromoOpen(false); }}
        />
      )}

      {/* Share-current-rank: build a synthetic promotion from current rank state.
          The card renders in "current" mode so the headline reads "I TRAIN" not
          "TEMPERED TO" — it's a state share, not a moment share. */}
      {shareCurrentOpen && currentRankObj && (
        <PromotionShareCard
          art={art}
          mode="current"
          premium={premium}
          promotion={{
            id: 'synthetic-current',
            rankId: currentRank.rankId,
            stripes: currentRank.stripes || 0,
            promotedAt: null,
          }}
          previousPromotion={null}
          athleteName={athleteName}
          onClose={() => setShareCurrentOpen(false)}
        />
      )}
    </div>
  );
};

// ---------- Promotion Timeline ----------
const PromotionTimeline = ({ art, promotions, athleteName, premium, onDelete }) => {
  const [sharing, setSharing] = useState(null); // { promotion, previousPromotion }
  // Walk the sorted promotions; tenure for each entry is "time between this and next" or "now"
  return (
    <div className="mt-5 pt-4 border-t border-enf-surface-border" data-testid="promotion-timeline">
      <div className="text-[10px] uppercase tracking-[0.25em] text-enf-text-muted font-semibold mb-3 flex items-center gap-1.5">
        <Trophy className="w-3 h-3 text-enf-accent-fg" /> Promotion log · {promotions.length}
      </div>
      <div className="space-y-3">
        {[...promotions].reverse().map((p, revIdx) => {
          const idx = promotions.length - 1 - revIdx;
          const rankObj = art.ranks.find((r) => r.id === p.rankId);
          const nextDate = promotions[idx + 1]?.promotedAt;
          const tenure = formatTenure(p.promotedAt, nextDate ? new Date(nextDate) : new Date());
          const isCurrent = idx === promotions.length - 1;
          const prevPromotion = idx > 0 ? promotions[idx - 1] : null;
          return (
            <div key={p.id} className="relative pl-4">
              {/* Timeline rail */}
              <div className="absolute left-0 top-2 bottom-0 w-px bg-enf-surface-elevated" style={{ display: revIdx === promotions.length - 1 ? 'none' : 'block' }} />
              <div className="absolute -left-[3px] top-2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: rankObj?.color || '#27272a', boxShadow: isCurrent ? `0 0 8px ${art.accent}` : 'none' }} />
              <div className="flex items-start gap-3">
                <div className="w-16 flex-shrink-0">
                  <BeltStrip rank={rankObj} stripes={p.stripes || 0} maxStripes={art.hasStripes ? art.maxStripes : 0} height="h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-bold text-enf-text-primary">{rankObj?.name || p.rankId}</span>
                    <span className="text-[10px] text-enf-text-muted font-mono whitespace-nowrap">{formatPromotionDate(p.promotedAt)}</span>
                  </div>
                  <div className="text-xs text-enf-text-muted">
                    {tenure} {isCurrent ? 'at rank' : 'held'}
                    {p.school && <span> · {p.school}</span>}
                    {p.by && <span> · by {p.by}</span>}
                  </div>
                  {p.notes && <div className="text-xs text-enf-text-secondary mt-1 italic">{p.notes}</div>}
                  {p.photo && (
                    <a href={p.photo} target="_blank" rel="noreferrer" className="mt-2 inline-block">
                      <img src={p.photo} alt={`${rankObj?.name} promotion`} className="w-20 h-20 object-cover rounded border border-enf-surface-border hover:border-enf-accent-fg transition-colors" />
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => setSharing({ promotion: p, previousPromotion: prevPromotion })}
                    className="text-enf-text-muted hover:text-enf-accent-fg transition-colors p-1"
                    aria-label="Share this promotion"
                    title="Share this promotion"
                    data-testid={`share-promotion-${p.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => { if (window.confirm('Remove this promotion from your log?')) onDelete(p.id); }}
                    className="text-enf-text-muted hover:text-enf-accent-fg transition-colors p-1"
                    aria-label="Delete promotion"
                    data-testid={`delete-promotion-${p.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {sharing && (
        <PromotionShareCard
          art={art}
          promotion={sharing.promotion}
          previousPromotion={sharing.previousPromotion}
          athleteName={athleteName}
          premium={premium}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  );
};

// ---------- Promotion Dialog ----------
const PromotionDialog = ({ art, currentRank, onClose, onSave }) => {
  // Default the new rank to the next one above current (the common case after a real promotion)
  const currentIdx = art.ranks.findIndex((r) => r.id === currentRank.rankId);
  const defaultRankId = currentIdx >= 0 && currentIdx < art.ranks.length - 1
    ? art.ranks[currentIdx + 1].id
    : currentRank.rankId || art.ranks[0].id;
  const today = new Date().toISOString().slice(0, 10);
  const [rankId, setRankId] = useState(defaultRankId);
  const [stripes, setStripes] = useState(0);
  const [promotedAt, setPromotedAt] = useState(today);
  const [school, setSchool] = useState('');
  const [by, setBy] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState('');

  const handlePhoto = async (input) => {
    setError('');
    if (!input) return;
    // Accepts EITHER a File (web file picker) OR a capture payload from
    // MediaCaptureMenu / native camera. Both paths land here.
    const isCapture = typeof input === 'object' && 'dataUrl' in input && !(input instanceof Blob);
    if (isCapture) {
      if (input.size > MAX_ATTACHMENT_BYTES) {
        setError('Photo over 3MB — try a different shot.');
        return;
      }
      setPhoto(input.dataUrl);
      return;
    }
    // File path
    if (input.size > MAX_ATTACHMENT_BYTES) {
      setError(`Photo over 3MB — compress and retry.`);
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(input);
    });
    setPhoto(dataUrl);
  };

  const save = () => {
    if (!rankId) { setError('Pick a rank.'); return; }
    if (!promotedAt) { setError('Pick a date.'); return; }
    onSave({
      rankId,
      stripes: art.hasStripes ? stripes : 0,
      promotedAt,
      school: school.trim() || undefined,
      by: by.trim() || undefined,
      notes: notes.trim() || undefined,
      photo: photo || undefined,
    });
  };

  const selectedRank = art.ranks.find((r) => r.id === rankId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="promotion-dialog">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-1 flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5" /> Promotion · {art.shortName}
            </div>
            <div className="font-display text-3xl tracking-wide leading-none">Mark the moment.</div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          {/* Preview the chosen belt */}
          {selectedRank && (
            <div data-testid="promotion-belt-preview">
              <BeltStrip
                rank={selectedRank}
                stripes={art.hasStripes ? stripes : 0}
                maxStripes={art.hasStripes ? art.maxStripes : 0}
                height="h-9"
                glowAccent={art.accent}
              />
              <div className="text-sm text-enf-text-secondary mt-2 font-semibold">{selectedRank.name}</div>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Promoted to</label>
            <div className="flex flex-wrap gap-1.5">
              {art.ranks.map((r) => (
                <button key={r.id} type="button" onClick={() => setRankId(r.id)}
                  data-testid={`promo-rank-${r.id}`}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                    rankId === r.id ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                  }`}>{r.name}</button>
              ))}
            </div>
          </div>

          {art.hasStripes && (
            <div>
              <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Stripes</label>
              <div className="flex gap-2">
                {Array.from({ length: art.maxStripes + 1 }).map((_, n) => (
                  <button key={n} type="button" onClick={() => setStripes(n)}
                    data-testid={`promo-stripe-${n}`}
                    className={`flex-1 py-2 rounded text-xs font-bold transition-colors ${
                      stripes === n ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-mid text-enf-text-secondary hover:text-enf-text-primary'
                    }`}>{n}</button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Date</label>
              <input type="date" value={promotedAt} max={today} onChange={(e) => setPromotedAt(e.target.value)}
                data-testid="promo-date"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">School / gym</label>
              <input type="text" value={school} onChange={(e) => setSchool(e.target.value)} placeholder="e.g. Atos HQ"
                data-testid="promo-school"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Promoted by</label>
            <input type="text" value={by} onChange={(e) => setBy(e.target.value)} placeholder="Professor / coach name (optional)"
              data-testid="promo-by"
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How it felt. Who was there. What it took to get here."
              data-testid="promo-notes"
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg min-h-[80px] resize-none" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Photo (3MB max)</label>
            {photo ? (
              <div className="flex items-center gap-3">
                <img src={photo} alt="Promotion" className="w-20 h-20 object-cover rounded border border-enf-surface-border" />
                <button type="button" onClick={() => setPhoto(null)}
                  className="text-xs text-enf-role-warning hover:text-enf-accent-fg transition-colors flex items-center gap-1">
                  <X className="w-3 h-3" /> Remove photo
                </button>
              </div>
            ) : (
              <MediaCaptureMenu
                onCapture={(payload) => handlePhoto(payload)}
                accept="photo"
                label="Add a photo of the moment"
                testId="promo-photo-upload"
                className="w-full p-3 border border-dashed border-enf-surface-border hover:border-enf-accent-fg rounded-md text-sm text-enf-text-secondary hover:text-enf-accent-fg transition-colors flex items-center justify-center gap-2"
              />
            )}
            {error && <div className="text-xs text-enf-role-warning mt-2">{error}</div>}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors">
            Cancel
          </button>
          <button onClick={save} data-testid="promo-save"
            className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
            <Trophy className="w-4 h-4" /> Save promotion
          </button>
        </div>
      </div>
    </div>
  );
};

const SessionCard = ({ session, art, onDelete, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const dt = new Date(session.date);
  // BJJ-rich sessions carry journal fields (intent/techniques/focusModes).
  // Detect on data shape rather than artId so any future BJJ-style art
  // forks (e.g. wrestling) light up the rich detail view automatically.
  const isRichJournal = !!(session.intent || (session.techniques && session.techniques.length > 0) || (session.focusModes && session.focusModes.length > 0));
  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-5 flex items-start gap-4 text-left hover:bg-enf-surface-mid/40 transition-colors">
        <div className="flex-shrink-0 text-center w-14 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md">
          <div className="text-xs text-enf-role-info uppercase font-semibold">{dt.toLocaleDateString(undefined, { month: 'short' })}</div>
          <div className="font-display text-2xl tracking-wide leading-none">{dt.getDate()}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-display text-2xl tracking-wide leading-none">
              {isRichJournal && session.intent ? session.intent : (session.focus || 'Training session')}
            </span>
            {session.attachments?.length > 0 && (
              <span className="text-xs text-enf-role-info flex items-center gap-1"><Paperclip className="w-3 h-3" /> {session.attachments.length}</span>
            )}
            {isRichJournal && session.techniques?.length > 0 && (
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-enf-role-info/10 border border-enf-role-info/30 text-enf-role-info font-semibold">
                {session.techniques.length} tech
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-enf-text-muted">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {session.duration} min</span>
            <span className="capitalize">{dt.toLocaleString(undefined, { weekday: 'long' })}</span>
            {isRichJournal && session.classFormat && (
              // classFormat is a string for Judo/Wrestling/Boxing (single-select) and
              // an array for BJJ/Muay Thai (multi-select). Normalize both shapes here
              // so the chip never blows up. Detail views render the polished label;
              // this card preview is best-effort underscores→spaces + capitalize.
              <span className="capitalize">
                {Array.isArray(session.classFormat)
                  ? session.classFormat.join(' + ')
                  : String(session.classFormat).replace(/_/g, ' ')}
              </span>
            )}
          </div>
          {!expanded && session.notes && <p className="text-sm text-enf-text-secondary mt-2 line-clamp-1">{session.notes}</p>}
        </div>
        <ChevronRight className={`w-4 h-4 text-enf-text-muted transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-enf-surface-border">
          {/* Rich-journal detail view — routes based on artId. New arts
              with their own forked schema get their detail component
              added here. */}
          {isRichJournal && session.artId === 'bjj' && <BJJSessionDetail session={session} />}
          {isRichJournal && session.artId === 'muay-thai' && <MuayThaiSessionDetail session={session} />}
          {isRichJournal && session.artId === 'judo' && <JudoSessionDetail session={session} />}
          {isRichJournal && session.artId === 'wrestling' && <WrestlingSessionDetail session={session} />}
          {isRichJournal && session.artId === 'boxing' && <BoxingSessionDetail session={session} />}
          {/* General notes (always shown when present, even for rich journal) */}
          {session.notes && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Notes</div>
              <p className="text-sm text-enf-text-primary whitespace-pre-wrap">{session.notes}</p>
            </div>
          )}
          {session.attachments?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Attachments ({session.attachments.length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {session.attachments.map((a, i) => <SharedAttachmentTile key={i} att={a} />)}
              </div>
            </div>
          )}
          <div className="mt-5 flex items-center gap-3">
            {onEdit && isRichJournal && (
              <button
                onClick={onEdit}
                data-testid={`session-edit-${session.id}`}
                className="px-3 py-1.5 text-xs font-semibold bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg text-enf-text-secondary hover:text-enf-text-primary rounded-md transition-colors flex items-center gap-1"
              >
                <Edit3 className="w-3 h-3" /> Edit session
              </button>
            )}
            <button
              onClick={onDelete}
              data-testid={`session-delete-${session.id}`}
              className="text-xs text-enf-text-muted hover:text-enf-accent-fg flex items-center gap-1 transition-colors"
            >
              <Trash2 className="w-3 h-3" /> Delete session
            </button>
          </div>
        </div>
      )}
    </div>
  );
};



export default TrainingPage;
